import React, { Component } from 'react';
import {
    Modal, ModalHeader, ModalBody, ModalFooter, Button, Table,
    FormGroup, Label, Input, FormText, Alert
} from 'reactstrap';
import moment from 'moment';

import DeleteModal from './deleteModal';
import IpWhitelistInput from './ipWhitelistInput';
import ApiKeyRevealModal from './apiKeyRevealModal';
import {
    API_KEY_STATUS,
    getApiKeyStatus,
    isApiKeyRevoked,
    maskApiKey,
    formatDate,
    formatDateTime,
    toTime,
} from '../helpers/apiKeys';

const GRACE_OPTIONS = [
    { value: 0, label: 'Immediately' },
    { value: 15, label: '15 minutes' },
    { value: 60, label: '1 hour' },
    { value: 1440, label: '24 hours' },
];

const EMPTY_FORM = { _id: null, name: '', ipWhitelist: [], expiresAt: '', isEnabled: false };

const errorMessage = (res, lang) => {
    if (res && res.result && res.result.error) {
        return res.result.error;
    }

    return 'Something went wrong, please try again.'.translate(lang);
};

const isForbidden = (res) => res && (res.status === 401 || res.status === 403);

/**
* API keys of one external client (VD-725).
* Keys authenticate with the X-API-KEY header, one key per device or
* integration. The plain key is only ever returned by create and rotate, so it
* is kept in state just long enough to show it once.
*/
class ApiKeys extends Component {
    constructor(props) {
        super(props);

        this.state = {
            items: [],
            loading: true,
            loadError: null,
            forbidden: false,
            canWrite: true,
            permissionNotice: null,
            showRevoked: false,
            form: null,
            formError: null,
            saving: false,
            rotateModal: null,
            rotateGrace: 0,
            revokeModal: null,
            actionError: null,
            reveal: null,
        };
    }

    componentDidMount() {
        this.load();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.clientId !== this.props.clientId) {
            this.setState({ items: [], loading: true, loadError: null, forbidden: false }, () => this.load());
        }
    }

    load = () => {
        if (!this.props.clientId) {
            return;
        }

        this.setState({ loading: true }, () => {
            this.props.apiAsync('apiKeys', { clientId: this.props.clientId }).then((res) => {
                if (isForbidden(res)) {
                    this.setState({ loading: false, forbidden: true, items: [] });
                    return;
                }

                if (!res || res.status >= 400) {
                    this.setState({ loading: false, loadError: errorMessage(res, this.props.lang), items: [] });
                    return;
                }

                let items = Array.isArray(res.result) ? res.result : [];

                // the API already sorts by createdAt desc, keep it stable anyway
                items = [...items].sort((a, b) => (toTime(b.createdAt) || 0) - (toTime(a.createdAt) || 0));

                this.setState({ loading: false, loadError: null, items: items });
            });
        });
    }

    /**
    * Shared handling for every write call. Returns true when the call went
    * through, otherwise it puts the backend message on screen.
    */
    handleWriteError = (res, setError) => {
        if (isForbidden(res)) {
            // the user may read the keys but not change them, drop the controls
            this.setState({
                canWrite: false,
                permissionNotice: 'You do not have permission to manage API keys for this client.'.translate(this.props.lang),
                form: null,
                formError: null,
                rotateModal: null,
                revokeModal: null,
                actionError: null,
            });
            return false;
        }

        if (!res || res.status >= 400) {
            setError(errorMessage(res, this.props.lang));
            return false;
        }

        return true;
    }

    /**
    * Create and rotate are the only calls that return the plain key. Keep it in
    * state only while the reveal modal is up.
    */
    revealFrom = (res, fallbackName) => {
        if (!res || !res.result || !res.result.apiKey) {
            return null;
        }

        return {
            apiKey: res.result.apiKey,
            name: res.result.data && res.result.data.name ? res.result.data.name : fallbackName,
        };
    }

    openCreate = () => {
        this.setState({ form: { ...EMPTY_FORM, ipWhitelist: [] }, formError: null });
    }

    openEdit = (item) => {
        this.setState({
            form: {
                _id: item._id,
                name: item.name ? item.name : '',
                ipWhitelist: item.ipWhitelist ? [...item.ipWhitelist] : [],
                expiresAt: item.expiresAt ? moment(item.expiresAt).format('YYYY-MM-DD') : '',
                isEnabled: item.isEnabled !== false,
                revoked: isApiKeyRevoked(item),
            },
            formError: null,
        });
    }

    updateForm = (field, value) => {
        this.setState({ form: { ...this.state.form, [field]: value }, formError: null });
    }

    /** A date picked in the form means "valid through the end of that day" */
    expiresAtPayload = (value) => {
        if (!value) {
            return null;
        }

        let date = moment(value, 'YYYY-MM-DD', true);

        return date.isValid() ? date.endOf('day').toDate().toISOString() : null;
    }

    save = () => {
        let form = this.state.form;

        if (!form) {
            return;
        }

        let name = form.name ? form.name.trim() : '';

        if (!name) {
            this.setState({ formError: 'Name is required!'.translate(this.props.lang) });
            return;
        }

        let expiresAt = this.expiresAtPayload(form.expiresAt);

        this.setState({ saving: true }, () => {
            if (form._id) {
                let payload = {
                    name: name,
                    ipWhitelist: form.ipWhitelist ? form.ipWhitelist : [],
                    expiresAt: expiresAt,
                };

                // a revoked key can never be enabled again, so the toggle is not sent either
                if (!form.revoked) {
                    payload.isEnabled = !!form.isEnabled;
                }

                this.props.apiAsync('apiKeyUpdate', { id: form._id, data: payload }).then((res) => {
                    this.setState({ saving: false });

                    if (!this.handleWriteError(res, (error) => this.setState({ formError: error }))) {
                        return;
                    }

                    this.setState({ form: null, formError: null }, () => this.load());
                });
            } else {
                let payload = {
                    name: name,
                    ipWhitelist: form.ipWhitelist ? form.ipWhitelist : [],
                    isEnabled: !!form.isEnabled,
                };

                if (expiresAt) {
                    payload.expiresAt = expiresAt;
                }

                this.props.apiAsync('apiKeyNew', { clientId: this.props.clientId, data: payload }).then((res) => {
                    this.setState({ saving: false });

                    if (!this.handleWriteError(res, (error) => this.setState({ formError: error }))) {
                        return;
                    }

                    this.setState({
                        form: null,
                        formError: null,
                        reveal: this.revealFrom(res, name),
                    }, () => this.load());
                });
            }
        });
    }

    rotate = () => {
        let item = this.state.rotateModal;

        if (!item) {
            return;
        }

        this.setState({ saving: true }, () => {
            this.props.apiAsync('apiKeyRotate', { id: item._id, data: { graceMinutes: Number(this.state.rotateGrace) } }).then((res) => {
                this.setState({ saving: false });

                if (!this.handleWriteError(res, (error) => this.setState({ actionError: error }))) {
                    this.setState({ rotateModal: null });
                    return;
                }

                this.setState({
                    rotateModal: null,
                    actionError: null,
                    reveal: this.revealFrom(res, item.name),
                }, () => this.load());
            });
        });
    }

    revoke = () => {
        let item = this.state.revokeModal;

        if (!item) {
            return;
        }

        this.props.apiAsync('apiKeyRevoke', { id: item._id }).then((res) => {
            if (!this.handleWriteError(res, (error) => this.setState({ actionError: error }))) {
                this.setState({ revokeModal: null });
                return;
            }

            // the document stays in the list as revoked, so reveal it even when the
            // filter is off - otherwise the row would just disappear
            this.setState({ revokeModal: null, actionError: null, showRevoked: true }, () => this.load());
        });
    }

    renderIpWhitelist = (item) => {
        let entries = item.ipWhitelist ? item.ipWhitelist : [];

        if (!entries.length) {
            return <span className="api-keys-muted">{'Any IP'.translate(this.props.lang)}</span>;
        }

        let visible = entries.slice(0, 2);

        return (
            <div className="api-keys-chips">
                {visible.map((entry) => <span className="ip-whitelist-chip" key={entry}>{entry}</span>)}
                {entries.length > visible.length ?
                    <span className="ip-whitelist-chip" title={entries.join(', ')}>
                        +{entries.length - visible.length}
                    </span>
                    : null}
            </div>
        );
    }

    renderForm = () => {
        let form = this.state.form;

        if (!form) {
            return null;
        }

        let editing = !!form._id;

        return (
            <Modal isOpen={true} toggle={() => this.setState({ form: null, formError: null })} size="lg">
                <ModalHeader toggle={() => this.setState({ form: null, formError: null })}>
                    {editing ? 'Edit API key'.translate(this.props.lang) : 'New API key'.translate(this.props.lang)}
                </ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label size="sm">{'Name'.translate(this.props.lang)}</Label>
                        <Input
                            type="text"
                            value={form.name}
                            placeholder={'ERP server'.translate(this.props.lang)}
                            onChange={(e) => this.updateForm('name', e.target.value)}
                        />
                        <FormText color="muted">
                            {'Describe the device or integration the key belongs to.'.translate(this.props.lang)}
                        </FormText>
                    </FormGroup>

                    <IpWhitelistInput
                        label={'IP whitelist'.translate(this.props.lang)}
                        lang={this.props.lang}
                        value={form.ipWhitelist}
                        onChange={(value) => this.updateForm('ipWhitelist', value)}
                    />

                    <FormGroup>
                        <Label size="sm">{'Expiry date'.translate(this.props.lang)}</Label>
                        <div className="api-keys-date-row">
                            <Input
                                type="date"
                                value={form.expiresAt}
                                onChange={(e) => this.updateForm('expiresAt', e.target.value)}
                            />
                            {form.expiresAt ?
                                <Button color="link" onClick={() => this.updateForm('expiresAt', '')}>
                                    {'Clear'.translate(this.props.lang)}
                                </Button>
                                : null}
                        </div>
                        <FormText color="muted">
                            {'Leave empty for a key that never expires.'.translate(this.props.lang)}
                        </FormText>
                    </FormGroup>

                    {!form.revoked ?
                        <FormGroup check>
                            <Label check>
                                <Input
                                    type="checkbox"
                                    checked={!!form.isEnabled}
                                    onChange={(e) => this.updateForm('isEnabled', e.target.checked)}
                                />
                                {' '}
                                {editing
                                    ? 'Active'.translate(this.props.lang)
                                    : 'Activate right away'.translate(this.props.lang)}
                            </Label>
                            {!editing ?
                                <FormText color="muted">
                                    {'A key that is not activated is created but rejected until you enable it.'.translate(this.props.lang)}
                                </FormText>
                                : null}
                        </FormGroup>
                        :
                        <Alert color="secondary" className="api-keys-inline-alert">
                            {'This key is revoked and cannot be activated again.'.translate(this.props.lang)}
                        </Alert>
                    }

                    {this.state.formError ?
                        <p className="api-keys-error">{this.state.formError}</p>
                        : null}
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" disabled={this.state.saving} onClick={this.save}>
                        {editing ? 'Save'.translate(this.props.lang) : 'Create key'.translate(this.props.lang)}
                    </Button>
                    <Button color="secondary" onClick={() => this.setState({ form: null, formError: null })}>
                        {'Cancel'.translate(this.props.lang)}
                    </Button>
                </ModalFooter>
            </Modal>
        );
    }

    renderRotateModal = () => {
        let item = this.state.rotateModal;

        if (!item) {
            return null;
        }

        let grace = Number(this.state.rotateGrace);

        return (
            <Modal isOpen={true} toggle={() => this.setState({ rotateModal: null })}>
                <ModalHeader toggle={() => this.setState({ rotateModal: null })}>
                    {'Rotate API key'.translate(this.props.lang)}
                </ModalHeader>
                <ModalBody>
                    <p>
                        {'A new key is generated for'.translate(this.props.lang)} <strong>{item.name}</strong>.
                        {' '}
                        {'The new key is shown only once, right after rotation.'.translate(this.props.lang)}
                    </p>

                    <FormGroup>
                        <Label size="sm">{'Grace period'.translate(this.props.lang)}</Label>
                        <Input
                            type="select"
                            value={this.state.rotateGrace}
                            onChange={(e) => this.setState({ rotateGrace: e.target.value })}
                        >
                            {GRACE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label.translate(this.props.lang)}
                                </option>
                            ))}
                        </Input>
                    </FormGroup>

                    <Alert color="warning" className="api-keys-inline-alert">
                        {grace === 0
                            ? 'The current key stops working the moment you confirm.'.translate(this.props.lang)
                            : `${'The current key keeps working until'.translate(this.props.lang)} ${moment().add(grace, 'minutes').format('YYYY-MM-DD HH:mm')}, ${'after that it stops working.'.translate(this.props.lang)}`}
                    </Alert>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" disabled={this.state.saving} onClick={this.rotate}>
                        {'Rotate'.translate(this.props.lang)}
                    </Button>
                    <Button color="secondary" onClick={() => this.setState({ rotateModal: null })}>
                        {'Cancel'.translate(this.props.lang)}
                    </Button>
                </ModalFooter>
            </Modal>
        );
    }

    render() {
        let lang = this.props.lang;
        let now = Date.now();

        if (this.state.forbidden) {
            return (
                <div className="api-keys-section">
                    <Alert color="info">
                        {'You do not have permission to see the API keys of this client.'.translate(lang)}
                    </Alert>
                </div>
            );
        }

        let items = this.state.items.map((item) => ({ item, status: getApiKeyStatus(item, lang, now) }));
        let revokedCount = items.filter((row) => row.status.code === API_KEY_STATUS.REVOKED).length;

        if (!this.state.showRevoked) {
            items = items.filter((row) => row.status.code !== API_KEY_STATUS.REVOKED);
        }

        let canWrite = this.state.canWrite;

        return (
            <div className="api-keys-section">
                <div className="api-keys-header">
                    <div>
                        <h3>{'API keys'.translate(lang)}</h3>
                        <p className="api-keys-muted">
                            {'One key per device or integration. Keys authenticate with the X-API-KEY header.'.translate(lang)}
                        </p>
                    </div>

                    <div className="api-keys-header-actions">
                        <Label check className="api-keys-filter">
                            <Input
                                type="checkbox"
                                checked={this.state.showRevoked}
                                onChange={(e) => this.setState({ showRevoked: e.target.checked })}
                            />
                            {' '}
                            {'Show revoked'.translate(lang)}
                            {revokedCount ? ` (${revokedCount})` : ''}
                        </Label>

                        {canWrite ?
                            <Button color="primary" onClick={this.openCreate}>
                                {'New key'.translate(lang)}
                            </Button>
                            : null}
                    </div>
                </div>

                {this.state.permissionNotice ?
                    <Alert color="info">{this.state.permissionNotice}</Alert>
                    : null}

                {this.state.loadError ?
                    <Alert color="danger">{this.state.loadError}</Alert>
                    : null}

                {this.state.actionError ?
                    <Alert color="danger" toggle={() => this.setState({ actionError: null })}>
                        {this.state.actionError}
                    </Alert>
                    : null}

                <div className="api-keys-table">
                    <Table responsive hover size="sm">
                        <thead>
                            <tr>
                                <th>{'NAME'.translate(lang)}</th>
                                <th>{'KEY'.translate(lang)}</th>
                                <th>{'STATUS'.translate(lang)}</th>
                                <th>{'IP WHITELIST'.translate(lang)}</th>
                                <th>{'EXPIRES'.translate(lang)}</th>
                                <th>{'LAST USED'.translate(lang)}</th>
                                <th>{'CREATED'.translate(lang)}</th>
                                {canWrite ? <th className="api-keys-actions-col">{'ACTIONS'.translate(lang)}</th> : null}
                            </tr>
                        </thead>
                        <tbody>
                            {this.state.loading ?
                                <tr>
                                    <td colSpan={canWrite ? 8 : 7} className="api-keys-muted">
                                        {'Loading...'.translate(lang)}
                                    </td>
                                </tr>
                                : null}

                            {!this.state.loading && !items.length ?
                                <tr>
                                    <td colSpan={canWrite ? 8 : 7} className="api-keys-muted">
                                        {revokedCount && !this.state.showRevoked
                                            ? 'No active keys. Tick "Show revoked" to see the revoked ones.'.translate(lang)
                                            : 'This client has no API keys yet.'.translate(lang)}
                                    </td>
                                </tr>
                                : null}

                            {!this.state.loading && items.map(({ item, status }) => {
                                let revoked = isApiKeyRevoked(item);

                                return (
                                    <tr key={item._id}>
                                        <td>{item.name}</td>
                                        <td>
                                            <code className="api-keys-mask">{maskApiKey(item)}</code>
                                            {item.rotatedAt ?
                                                <div className="api-keys-muted api-keys-sub">
                                                    {'rotated'.translate(lang)} {formatDateTime(item.rotatedAt)}
                                                </div>
                                                : null}
                                        </td>
                                        <td>
                                            <span className={status.className}>{status.label}</span>
                                            {status.note ?
                                                <div className="api-keys-muted api-keys-sub">{status.note}</div>
                                                : null}
                                        </td>
                                        <td>{this.renderIpWhitelist(item)}</td>
                                        <td>
                                            {item.expiresAt
                                                ? formatDate(item.expiresAt)
                                                : <span className="api-keys-muted">{'No expiry'.translate(lang)}</span>}
                                        </td>
                                        <td>
                                            {item.lastUsedAt ?
                                                <>
                                                    {formatDateTime(item.lastUsedAt)}
                                                    {item.lastUsedIp ?
                                                        <div className="api-keys-muted api-keys-sub">{item.lastUsedIp}</div>
                                                        : null}
                                                </>
                                                :
                                                <span className="api-keys-muted">{'Never'.translate(lang)}</span>}
                                        </td>
                                        <td>
                                            {item.createdByUsername ? item.createdByUsername : <span className="api-keys-muted">-</span>}
                                            <div className="api-keys-muted api-keys-sub">{formatDateTime(item.createdAt)}</div>
                                        </td>

                                        {canWrite ?
                                            <td className="api-keys-actions-col">
                                                <div className="api-keys-actions">
                                                    <Button size="sm" color="secondary" onClick={() => this.openEdit(item)}>
                                                        {'Edit'.translate(lang)}
                                                    </Button>

                                                    {!revoked ?
                                                        <Button size="sm" color="secondary" onClick={() => this.setState({ rotateModal: item, rotateGrace: 0 })}>
                                                            {'Rotate'.translate(lang)}
                                                        </Button>
                                                        : null}

                                                    {!revoked ?
                                                        <Button size="sm" color="danger" onClick={() => this.setState({ revokeModal: item })}>
                                                            {'Revoke'.translate(lang)}
                                                        </Button>
                                                        : null}
                                                </div>
                                            </td>
                                            : null}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>

                <div className="api-keys-usage">
                    <h4>{'How the client sends the key'.translate(lang)}</h4>
                    <pre>{'X-API-KEY: <key>'}</pre>
                    <p className="api-keys-muted">
                        {'Basic authentication still works, but it is planned to be turned off - new integrations should use the header.'.translate(lang)}
                    </p>
                </div>

                {this.renderForm()}
                {this.renderRotateModal()}

                <DeleteModal
                    lang={lang}
                    isOpen={!!this.state.revokeModal}
                    toggle={() => this.setState({ revokeModal: null })}
                    handler={this.revoke}
                >
                    {'Revoke key'.translate(lang)} <strong>{this.state.revokeModal ? this.state.revokeModal.name : ''}</strong>?
                    <br />
                    {'The key stops working immediately. This action cannot be undone.'.translate(lang)}
                </DeleteModal>

                <ApiKeyRevealModal
                    lang={lang}
                    apiKey={this.state.reveal ? this.state.reveal.apiKey : null}
                    name={this.state.reveal ? this.state.reveal.name : null}
                    onClose={() => this.setState({ reveal: null })}
                />
            </div>
        );
    }
}

export default ApiKeys;
